import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    CircularProgress,
    TablePagination,
} from '@mui/material';
import { CSVLink } from "react-csv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { axios_tenant } from "../../../axios";
import { toast } from 'react-toastify';
import ExcelJS from "exceljs";


export default function MyList() {
    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const email = localStorage.getItem('TENANT_USER_MAIL');

    const fetchList = async () => {
        setLoading(true);
        try {
            const res = await axios_tenant.get(`/file-number-generator/list?email=${email}`);
            setList(res.data.data || []);
        } catch (e) {
            toast.error('Failed to fetch list');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
        // eslint-disable-next-line
    }, []);

    // CSV columns and data
    const columns = [
        { field: "reason", headerName: "Family Number Request Basis" },
        { field: "subOrg", headerName: "Sub-org" },
        { field: "matterType", headerName: "Matter/IP Type" },
        { field: "sequence", headerName: "Family Number Generated" },
        { field: "generatedAt", headerName: "Date/Time Family Number Generated" },
        // { field: "countryCode", headerName: "Country Code" },
        // { field: "status", headerName: "Status" },
    ];

    const csvData = list.map(row => {
        let newRow = {};
        columns.forEach(col => {
            if (col.field === "generatedAt") {
                newRow[col.headerName] = row.generatedAt
                    ? new Date(row.generatedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                    : "";
            } else {
                newRow[col.headerName] = row[col.field] || "-";
            }
        });
        return newRow;
    });

    // Excel export handler
    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Family Numbers");

        // Add header row
        worksheet.addRow(columns.map(col => col.headerName));

        // Add data rows
        list.forEach(row => {
            worksheet.addRow([
                row.reason || "-",
                row.subOrg || "-",
                row.matterType || "-",
                row.sequence || "-",
                row.generatedAt
                    ? new Date(row.generatedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                    : "-"
            ]);
        });

        // Style header
        worksheet.getRow(1).eachCell(cell => {
            cell.font = { bold: true, color: { argb: "FF1A237E" } };
        });

        // Style unused rows
        list.forEach((row, idx) => {
            if (!row.status?.used) {
                const excelRow = worksheet.getRow(idx + 2); // +2 because header is row 1
                excelRow.eachCell(cell => {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFEEEEEE" } // light grey
                    };
                    cell.font = {
                        color: { argb: "FF444444" } // dark grey
                    };
                });
            }
        });

        // Auto width for columns
        worksheet.columns.forEach(column => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, cell => {
                const cellValue = cell.value ? cell.value.toString() : "";
                maxLength = Math.max(maxLength, cellValue.length);
            });
            column.width = maxLength + 2;
        });

        // Download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "my_generated_family_numbers.xlsx";
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Paginated data
    const paginatedList = list.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box sx={{
            maxWidth: 1200,
            mx: 'auto'
        }}>
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 600 }}>
                My Generated Family Numbers
            </Typography>
            <Paper sx={{
                p: 2,
                borderRadius: 3,
                background: "#fafdff",
                boxShadow: "0 2px 8px 0 rgba(60,72,100,0.07)"
            }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<FileDownloadIcon />}
                        sx={{ p:1 }}
                        onClick={handleExportExcel}
                    >
                        Download Excel
                    </Button>
                </Box>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer component={Paper} sx={{
                            boxShadow: "none",
                            borderRadius: 2,
                            background: "#fff",
                            border: "1px solid #e0e3e7"
                        }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ background: "#f4f8fb" }}>
                                        <TableCell sx={{ fontWeight: 600, color: "#1a237e" }}>
                                            Family Number Request Basis
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, color: "#1a237e" }}>
                                            Sub-org
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, color: "#1a237e" }}>
                                            Matter/IP Type
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, color: "#1a237e" }}>
                                            Family Number Generated
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, color: "#1a237e" }}>
                                            Date/Time Family Number Generated
                                        </TableCell>
                                        {/* <TableCell sx={{ fontWeight: 600 }}>Country Code</TableCell> */}
                                        {/* <TableCell sx={{ fontWeight: 600 }}>Status</TableCell> */}
                                        {/* <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell> */}
                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{
                                    '&, & *': {
                                            fontSize: 16, // This will apply font size to all descendants
                                        },
                                }}>
                                    {paginatedList.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                No records found.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedList.map(row => (
                                            <TableRow
                                                key={row.sequence}
                                                style={{
                                                    backgroundColor: row.status?.used ? '' : '#eee',
                                                    pointerEvents: row.status?.used ? '' : 'none',
                                                    opacity: row.status?.used ? 1 : 0.5
                                                }}
                                            >
                                                <TableCell>{row.reason || '-'}</TableCell>
                                                <TableCell align="center">{row.subOrg}</TableCell>
                                                <TableCell align="center">{row.matterType}</TableCell>
                                                <TableCell align="center">{row.sequence}</TableCell>
                                                <TableCell align="center">
                                                    {row.generatedAt
                                                        ? new Date(row.generatedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                                                        : "-"}
                                                </TableCell>
                                                {/* <TableCell>{row.countryCode}</TableCell> */}
                                                {/* <TableCell>
                                                    {row.status?.used
                                                        ? 'Used'
                                                        : row.status?.deleted
                                                        ? 'Deleted'
                                                        : 'Available'}
                                                </TableCell> */}
                                                {/* <TableCell align="center">
                                                    {!row.status?.used && !row.status?.deleted && (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                sx={{ mr: 1 }}
                                                                disabled={!!actionLoading[row.sequence]}
                                                                onClick={() => handleAction(row.sequence, 'save')}
                                                            >
                                                                {actionLoading[row.sequence] ? (
                                                                    <CircularProgress size={18} />
                                                                ) : (
                                                                    'Save'
                                                                )}
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="error"
                                                                disabled={!!actionLoading[row.sequence]}
                                                                onClick={() => handleAction(row.sequence, 'delete')}
                                                            >
                                                                {actionLoading[row.sequence] ? (
                                                                    <CircularProgress size={18} />
                                                                ) : (
                                                                    'Delete'
                                                                )}
                                                            </Button>
                                                        </>
                                                    )}
                                                </TableCell> */}
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={list.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 20, 50, 100]}
                            sx={{
                                '.MuiTablePagination-toolbar': {
                                    flexWrap: 'nowrap',
                                    gap: 1,
                                    minHeight: 56,
                                },
                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                    margin: 0,
                                }
                            }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );
}